import {
  useTranslation
} from "../../../../chunk-6N4NEKWU.mjs";
import "../../../../chunk-D57E6H3M.mjs";

// src/components/composite/Timeline/ClarifyingQuestions/ClarifyingQuestions.tsx
import { useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from "react";
import { isImeComposing } from "../../../../utils/isImeComposing.mjs";
import { Button, Checkbox, Field, RadioGroup, RadioGroupItem, Text, TextField } from "../../../primitive/index.mjs";
import { StableLabel } from "../../../primitive/Button/StableLabel.mjs";
import { ClarifyingQuestionsHeader } from "./ClarifyingQuestionsHeader.mjs";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var OTHER_SENTINEL = "__other__";
var NOTHING_PICKED = [];
var ARROW_KEYS = /* @__PURE__ */ new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"]);
var CHOICE_SELECTOR = '[role="radio"], [role="checkbox"]';
function answerFor(question, picked, otherText) {
  const answers = question.options.filter((option) => picked.includes(option));
  if (picked.includes(OTHER_SENTINEL)) answers.push(otherText.trim());
  return answers.join(", ");
}
function summaryAnswerClass(skipped) {
  return skipped ? "buyscout-clarifying-summary-answer buyscout-clarifying-summary-answer--skipped" : "buyscout-clarifying-summary-answer";
}
var CHEVRON_UP = /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: /* @__PURE__ */ jsx("polyline", { points: "18 15 12 9 6 15" }) });
var CHEVRON_DOWN = /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: /* @__PURE__ */ jsx("polyline", { points: "6 9 12 15 18 9" }) });
function ClarifyingQuestions({ ref, questions, onComplete, total: totalExpected, turnFailed = false, dashboardMode = false }) {
  const { t } = useTranslation();
  const instanceId = useId();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [turn, setTurn] = useState(null);
  const [answers, setAnswers] = useState(() => /* @__PURE__ */ new Map());
  const [otherTexts, setOtherTexts] = useState(() => /* @__PURE__ */ new Map());
  const [completedAnswers, setCompletedAnswers] = useState(null);
  const otherInputRef = useRef(null);
  const pagesRef = useRef(null);
  const selectingByArrowRef = useRef(false);
  const [otherFocusRequest, setOtherFocusRequest] = useState(0);
  const focusNextPageRef = useRef(false);
  const pagerRef = useRef(null);
  const keepPagerFocusRef = useRef(false);
  const cardRef = useRef(null);
  const titleRef = useRef(null);
  const focusSummaryRef = useRef(false);
  const [sentHere, setSentHere] = useState(false);
  const [queuedAdvance, setQueuedAdvance] = useState(null);
  const questionCount = questions.length;
  const isCutShort = totalExpected != null && totalExpected > 0 && questionCount < totalExpected;
  const isLoadingMore = isCutShort && !turnFailed;
  const waitingForNext = isLoadingMore && currentIndex >= questionCount - 1;
  const pageCount = turnFailed ? questionCount : Math.max(questionCount, totalExpected ?? 0);
  const isLastQuestion = currentIndex >= pageCount - 1;
  const cannotSubmit = isCutShort && turnFailed && currentIndex >= questionCount - 1;
  const isPreAnswered = questions.some((q) => q.answer != null || q.isSkipped === true);
  useImperativeHandle(ref, () => ({
    skipAll() {
      if (completedAnswers) return;
      const result = questions.map((_, i) => ({
        questionIndex: i,
        answer: "",
        skipped: true
      }));
      setCompletedAnswers(result);
      onComplete(result);
    }
  }), [questions, onComplete, completedAnswers]);
  const question = questions[currentIndex];
  const picked = answers.get(currentIndex) ?? NOTHING_PICKED;
  const isOtherSelected = picked.includes(OTHER_SENTINEL);
  const canContinue = picked.length > 0 && (!isOtherSelected || (otherTexts.get(currentIndex) ?? "").trim().length > 0);
  useEffect(() => {
    if (otherFocusRequest > 0) otherInputRef.current?.focus();
  }, [otherFocusRequest]);
  useEffect(() => {
    const focusChoice = () => {
      const page = pagesRef.current?.children[currentIndex];
      const choice = page?.querySelector('[role="radio"][data-state="checked"], [role="checkbox"][data-state="checked"]') ?? page?.querySelector(CHOICE_SELECTOR);
      choice?.focus();
    };
    if (focusNextPageRef.current) {
      focusNextPageRef.current = false;
      focusChoice();
    }
    if (keepPagerFocusRef.current) {
      keepPagerFocusRef.current = false;
      if (pagerRef.current?.querySelector(":focus:enabled")) return;
      const other = pagerRef.current?.querySelector("button:enabled");
      if (other) other.focus();
      else focusChoice();
    }
  }, [currentIndex]);
  const handleSelect = (value) => {
    setAnswers((prev) => new Map(prev).set(currentIndex, [value]));
    if (value === OTHER_SENTINEL && !selectingByArrowRef.current) setOtherFocusRequest((n) => n + 1);
  };
  const handleToggle = (value, checked) => {
    if (value === OTHER_SENTINEL && checked) setOtherFocusRequest((n) => n + 1);
    setAnswers((prev) => {
      const current = prev.get(currentIndex) ?? NOTHING_PICKED;
      const next = checked ? [...current.filter((item) => item !== value), value] : current.filter((item) => item !== value);
      return new Map(prev).set(currentIndex, next);
    });
  };
  const turnTo = (index) => {
    keepPagerFocusRef.current = pagerRef.current?.matches(":focus-within") ?? false;
    setQueuedAdvance(null);
    setTurn({ from: currentIndex, direction: index > currentIndex ? "next" : "prev" });
    setCurrentIndex(index);
  };
  const advance = useCallback((skipped) => {
    if (currentIndex < questionCount - 1) {
      focusNextPageRef.current = cardRef.current?.matches(":focus-within") ?? false;
      setTurn({ from: currentIndex, direction: "next" });
      setCurrentIndex(currentIndex + 1);
    } else {
      const result = questions.map((q, i) => {
        const ans = answers.get(i);
        const wasSkipped = skipped && i === currentIndex ? true : ans == null || ans.length === 0;
        const finalAnswer = wasSkipped || ans == null ? "" : answerFor(q, ans, otherTexts.get(i) ?? "");
        return { questionIndex: i, answer: finalAnswer, skipped: wasSkipped };
      });
      focusSummaryRef.current = cardRef.current?.matches(":focus-within") ?? false;
      setSentHere(true);
      setCompletedAnswers(result);
      onComplete(result);
    }
  }, [currentIndex, questionCount, questions, answers, otherTexts, onComplete]);
  useEffect(() => {
    if (!completedAnswers || !focusSummaryRef.current) return;
    focusSummaryRef.current = false;
    titleRef.current?.focus();
  }, [completedAnswers]);
  useEffect(() => {
    if (!queuedAdvance || waitingForNext || completedAnswers) return;
    setQueuedAdvance(null);
    if (!turnFailed) {
      if (queuedAdvance === "skip" || canContinue) advance(false);
      return;
    }
    const active = cardRef.current?.getRootNode()?.activeElement;
    if (cannotSubmit && (!active || active === document.body)) titleRef.current?.focus();
  }, [queuedAdvance, waitingForNext, completedAnswers, turnFailed, cannotSubmit, canContinue, advance]);
  const handleContinue = () => {
    if (!canContinue) return;
    if (waitingForNext) setQueuedAdvance("continue");
    else advance(false);
  };
  const handleSkip = () => {
    setAnswers((prev) => {
      const next = new Map(prev);
      next.delete(currentIndex);
      return next;
    });
    if (waitingForNext) setQueuedAdvance("skip");
    else advance(true);
  };
  const handleOtherTextChange = (text) => {
    setOtherTexts((prev) => new Map(prev).set(currentIndex, text));
  };
  const handleOtherTextKeyDown = (event) => {
    if (event.key !== "Enter" || isImeComposing(event.nativeEvent)) return;
    event.preventDefault();
    handleContinue();
  };
  if (dashboardMode && questions.length > 0) {
    return /* @__PURE__ */ jsxs("div", { className: "buyscout-clarifying buyscout-clarifying--completed", children: [
      /* @__PURE__ */ jsx(ClarifyingQuestionsHeader, {}),
      /* @__PURE__ */ jsx("div", { className: "buyscout-clarifying-summary", children: questions.map((q, i) => /* @__PURE__ */ jsxs(Field, { layout: "stacked", dir: "auto", label: `${i + 1}. ${q.question}`, children: [
        q.options?.length ? /* @__PURE__ */ jsx(Text, { variant: "caption", as: "div", children: `${t("clarifyingQuestions.options")}: ${q.options.join(", ")}` }) : null,
        (q.answer != null || q.isSkipped === true) && /* @__PURE__ */ jsx("div", { className: summaryAnswerClass(q.isSkipped === true), children: `${t("clarifyingQuestions.userAnswer")}: ${q.isSkipped ? t("clarifyingQuestions.skipped") : q.answer ?? ""}` })
      ] }, i)) })
    ] });
  }
  if (!questions.length) return null;
  const status = /* @__PURE__ */ jsx("div", { className: "buyscout-clarifying-status", role: "status", children: sentHere ? t("clarifyingQuestions.sent") : "" });
  const summary = isPreAnswered ? questions.map((q) => ({ skipped: q.isSkipped === true, answer: q.answer ?? "" })) : completedAnswers;
  if (summary) {
    return /* @__PURE__ */ jsxs("div", { className: "buyscout-clarifying buyscout-clarifying--completed", children: [
      status,
      /* @__PURE__ */ jsx(ClarifyingQuestionsHeader, { titleRef }),
      /* @__PURE__ */ jsx("div", { className: "buyscout-clarifying-summary", children: questions.map((q, i) => /* @__PURE__ */ jsx(Field, { layout: "stacked", dir: "auto", label: `${i + 1}. ${q.question}`, children: /* @__PURE__ */ jsx("div", { className: summaryAnswerClass(summary[i].skipped), children: summary[i].skipped ? t("clarifyingQuestions.skipped") : summary[i].answer }) }, i)) })
    ] });
  }
  if (!question) return null;
  return /* @__PURE__ */ jsxs("div", { ref: cardRef, className: "buyscout-clarifying", children: [
    status,
    /* @__PURE__ */ jsx(ClarifyingQuestionsHeader, { titleRef, children: /* @__PURE__ */ jsxs("div", { ref: pagerRef, className: "buyscout-clarifying-nav", children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          variant: "borderless",
          color: "neutral",
          size: "xs",
          iconOnly: true,
          disabled: currentIndex === 0,
          onClick: () => turnTo(currentIndex - 1),
          "aria-label": t("clarifyingQuestions.prevQuestion"),
          children: CHEVRON_UP
        }
      ),
      /* @__PURE__ */ jsxs(Text, { variant: "body-sm", as: "span", className: "buyscout-clarifying-nav-counter", children: [
        currentIndex + 1,
        " ",
        t("clarifyingQuestions.navOf"),
        " ",
        isLoadingMore && totalExpected != null ? totalExpected : questionCount
      ] }),
      /* @__PURE__ */ jsx(
        Button,
        {
          variant: "borderless",
          color: "neutral",
          size: "xs",
          iconOnly: true,
          loading: waitingForNext,
          disabled: !waitingForNext && currentIndex >= questionCount - 1,
          onClick: () => turnTo(currentIndex + 1),
          "aria-label": waitingForNext ? t("clarifyingQuestions.loadingNext") : t("clarifyingQuestions.nextQuestion"),
          children: CHEVRON_DOWN
        }
      )
    ] }) }),
    /* @__PURE__ */ jsx("div", { ref: pagesRef, className: "buyscout-clarifying-pages", "data-direction": turn?.direction, children: questions.map((q, index) => {
      const current = index === currentIndex;
      const pickedHere = answers.get(index) ?? NOTHING_PICKED;
      const otherPicked = pickedHere.includes(OTHER_SENTINEL);
      const questionId = `${instanceId}-${index}-question`;
      const otherId = `${instanceId}-${index}-other`;
      const control = (id, value, ariaLabel) => q.multi ? /* @__PURE__ */ jsx(Checkbox, { id, size: "sm", "aria-label": ariaLabel, checked: pickedHere.includes(value), onCheckedChange: (checked) => handleToggle(value, checked === true) }) : /* @__PURE__ */ jsx(RadioGroupItem, { id, value, size: "sm", "aria-label": ariaLabel });
      const options = /* @__PURE__ */ jsxs(Fragment, { children: [
        q.options.map((option, optionIndex) => {
          const id = `${instanceId}-${index}-${optionIndex}`;
          return /* @__PURE__ */ jsxs("label", { htmlFor: id, className: "buyscout-clarifying-option", children: [
            control(id, option),
            /* @__PURE__ */ jsx(Text, { variant: "body", as: "span", className: "buyscout-clarifying-option-label", dir: "auto", children: option })
          ] }, id);
        }),
        /* @__PURE__ */ jsxs("div", { className: "buyscout-clarifying-option", "data-typing": otherPicked || void 0, children: [
          control(otherId, OTHER_SENTINEL, otherPicked ? t("clarifyingQuestions.otherOption") : void 0),
          otherPicked ? /* @__PURE__ */ jsx(
            TextField,
            {
              ref: current ? otherInputRef : void 0,
              size: "sm",
              fullWidth: true,
              className: "buyscout-timeline-field",
              placeholder: t("clarifyingQuestions.answerPlaceholder"),
              "aria-label": t("clarifyingQuestions.otherAnswerAria"),
              value: otherTexts.get(index) ?? "",
              onChange: (e) => handleOtherTextChange(e.target.value),
              onKeyDown: handleOtherTextKeyDown
            }
          ) : /* @__PURE__ */ jsx("label", { htmlFor: otherId, className: "buyscout-clarifying-option-label buyscout-clarifying-other-label", children: /* @__PURE__ */ jsx(Text, { variant: "body", as: "span", children: t("clarifyingQuestions.otherLabel") }) })
        ] })
      ] });
      return /* @__PURE__ */ jsxs("div", { className: "buyscout-clarifying-page", inert: !current, "aria-hidden": current ? void 0 : true, "data-leaving": !current && turn?.from === index || void 0, children: [
        /* @__PURE__ */ jsxs("div", { className: "buyscout-clarifying-question", id: questionId, dir: "auto", children: [
          /* @__PURE__ */ jsxs(Text, { variant: "body-bold", as: "span", className: "buyscout-clarifying-question-number", children: [
            index + 1,
            "."
          ] }),
          /* @__PURE__ */ jsx(Text, { variant: "body", as: "span", children: q.question })
        ] }),
        q.multi ? /* @__PURE__ */ jsx("div", { role: "group", "aria-labelledby": questionId, className: "buyscout-clarifying-options", children: options }) : /* @__PURE__ */ jsx(
          RadioGroup,
          {
            className: "buyscout-clarifying-options",
            "aria-labelledby": questionId,
            value: pickedHere[0] ?? "",
            onValueChange: handleSelect,
            onKeyDownCapture: (event) => {
              selectingByArrowRef.current = ARROW_KEYS.has(event.key);
            },
            onPointerDownCapture: () => {
              selectingByArrowRef.current = false;
            },
            children: options
          }
        )
      ] }, index);
    }) }),
    /* @__PURE__ */ jsxs("div", { className: "buyscout-clarifying-footer", children: [
      /* @__PURE__ */ jsx(Button, { variant: "borderless", color: "neutral", type: "button", size: "sm", className: "buyscout-clarifying-skip", loading: queuedAdvance === "skip", disabled: cannotSubmit, onClick: handleSkip, children: t("clarifyingQuestions.skip") }),
      /* @__PURE__ */ jsx(
        Button,
        {
          variant: "prominent",
          type: "button",
          size: "sm",
          className: "buyscout-clarifying-continue",
          loading: queuedAdvance === "continue",
          disabled: !canContinue || cannotSubmit,
          onClick: handleContinue,
          children: /* @__PURE__ */ jsx(StableLabel, { labels: pageCount > 1 ? [t("clarifyingQuestions.continue"), t("clarifyingQuestions.submit")] : [t("clarifyingQuestions.submit")], children: isLastQuestion ? t("clarifyingQuestions.submit") : t("clarifyingQuestions.continue") })
        }
      )
    ] })
  ] });
}
export {
  ClarifyingQuestions
};
//# sourceMappingURL=ClarifyingQuestions.mjs.map